import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useConversationStoreShallow } from 'src/store/conversationStore';

import {
    ModalsProvider,
    notify,
    StepProgressBar,
} from '@speeki/global-ui-components';

import { ConversationLayout } from '@components/templates/ConversationLayout/ConversationLayout';

import { CaseSummarizeDocument } from '@graphql/generated/graphql';
import { assistantResponse } from '@graphql/helpers';

import { useResponsive } from '@utils/hooks/useResponsive';

import { getPath } from '@services/RouteService/paths';

import { useMutation } from '@apollo/client';
import { currentLanguageVar } from '@boot/reactiveVariables';
import { ConversationChatDisplay } from '@domains/new-conversation/organisms/ConversationChatDisplay/ConversationChatDisplay';
import { ReportConversationForm } from '@domains/new-conversation/organisms/ReportConversationForm/ReportConversationForm';
import {
    ProceedButton,
    ProceedWrapper,
} from '@domains/new-conversation/pages/Conversation.styled';
import {
    ChatWrapper,
    ProgressBarWrapper,
} from '@domains/new-conversation/pages/ReportConversationPage/ReportConversationPage.styled';
import { useConversationRedirection } from '@domains/new-conversation/useConversationRedirection';
import { ERROR_MESSAGES } from '@shared/notifyMessages';

export const ReportConversationPage = () => {
    useConversationRedirection();
    const navigate = useNavigate();

    const { caseUuid } = useParams<{ caseUuid: string }>();

    const { t } = useTranslation();
    const isMd = useResponsive('md');

    const caseResponse = assistantResponse(caseUuid ?? '');
    const { stepLabelTranslated, stepOrder, stepsCount } =
        caseResponse?.progress || {};

    const { loading: conversationLoading, messages } =
        useConversationStoreShallow(['loading', 'messages']);
    const conversationLength = messages.length;

    const [summarizeCase, { loading: summaryLoading }] = useMutation(
        CaseSummarizeDocument,
        {
            context: {
                notifyOnError: {
                    exceptions: ['CONVERSATION_NOT_DESCRIBED'],
                },
            },
            onCompleted: (data) => {
                navigate(
                    getPath({
                        params: { caseUuid: data?.summary.uuid },
                        path: '/new-conversation/:caseUuid/review',
                    }),
                );
            },
            onError: (error) => {
                const requiredInfoNotProvided =
                    error.graphQLErrors[0]?.extensions?.code ===
                    'CONVERSATION_NOT_DESCRIBED';

                requiredInfoNotProvided &&
                    notify(
                        t(ERROR_MESSAGES.newReport.requiredInfoNotProvided),
                    ).ERROR();
            },
        },
    );

    const isLoading = conversationLoading || summaryLoading;

    const handleSummarize = () =>
        summarizeCase({
            variables: {
                input: { uuid: caseUuid ?? '' },
                targetLanguage: currentLanguageVar(),
            },
        });

    const stepCounter =
        caseResponse?.progress && stepsCount ? (
            <ProgressBarWrapper>
                <StepProgressBar
                    currentStep={stepOrder || 0}
                    currentStepText={`${t('STEP')} ${stepOrder} ${t(
                        'OF',
                    )} ${stepsCount}`}
                    numberOfSteps={stepsCount}
                    testId="BG6FpzA"
                    title={stepLabelTranslated || ''}
                />
            </ProgressBarWrapper>
        ) : undefined;

    return (
        <ConversationLayout
            footer={<ReportConversationForm />}
            header={isMd ? stepCounter : undefined}
            sidebarProps={{
                buttons: (
                    <ProceedWrapper>
                        <ProceedButton
                            disabled={isLoading || summaryLoading}
                            onClick={() =>
                                ModalsProvider.open(
                                    'ConversationDetailsGoBackModal',
                                )
                            }
                            testId="button_back"
                            variant={'secondary'}
                        >
                            {t('BACK')}
                        </ProceedButton>
                        <ProceedButton
                            disabled={
                                isLoading ||
                                !conversationLength ||
                                !caseResponse?.isDescribed
                            }
                            isLoading={summaryLoading}
                            onClick={handleSummarize}
                            testId="button_next"
                            variant={'primary'}
                        >
                            {t('NEXT')}
                        </ProceedButton>
                    </ProceedWrapper>
                ),
                currentStep: 2,
                preventionModalActive: true,
            }}
        >
            <ChatWrapper>
                {!isMd && stepCounter}
                <ConversationChatDisplay />
                {!isMd && <ReportConversationForm />}
            </ChatWrapper>
        </ConversationLayout>
    );
};
